import { useState, useEffect, useRef, useCallback } from 'react'
import req from '@sportninja/common/api/request'
import { OrganizationTreeNode } from 'src/context/MyScheduleContext/types'

export type { OrganizationTreeNode }

export const REFRESH_ORG_TREE_EVENT = 'refresh-org-tree'

interface UseOrganizationsTreeReturn {
  data: OrganizationTreeNode[] | null
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to fetch the user's organizations tree hierarchy
 * Uses req directly with a ref flag to prevent duplicate API calls
 * Listens for 'refresh-org-tree' custom event to re-fetch
 */
export function useOrganizationsTree(): UseOrganizationsTreeReturn {
  const [data, setData] = useState<OrganizationTreeNode[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const fetchedRef = useRef(false)

  const fetchTree = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await req(
        '/users/me2/organizations/tree?include_schedules=true'
      )
      setData(response.data)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (fetchedRef.current) {
      return
    }
    fetchedRef.current = true
    fetchTree()
  }, [fetchTree])

  useEffect(() => {
    const handleRefresh = () => {
      fetchTree()
    }
    window.addEventListener(REFRESH_ORG_TREE_EVENT, handleRefresh)
    return () => {
      window.removeEventListener(REFRESH_ORG_TREE_EVENT, handleRefresh)
    }
  }, [fetchTree])

  return { data, isLoading, error }
}

export default useOrganizationsTree
